'use client';

import type { ReactNode } from 'react';
import { defaultAuthorAtom } from 'app/blog/[domain]/_components/design-state';
import { PostAvatar } from 'app/blog/[domain]/(standalone)/_components/post-avatar';
import type { BlogPost } from 'app/blog/[domain]/(standalone)/_server/types';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { PostAside } from './aside/post-aside';
import { PostBreadcrumb } from './header/breadcrumb';
import { CoverImage } from './header/cover-image';
import { PostDecorator } from './header/decorator';
import { Excerpt } from './header/excerpt';
import { PublishedDate } from './header/published-date';
import { Sharing } from './header/sharing';

export function FieldnotesReadingFrame({
  post,
  postUrl,
  children,
}: {
  post: BlogPost;
  postUrl: string;
  children: ReactNode;
}) {
  const author = useAtomValue(defaultAuthorAtom);
  const t = useTranslations('Blog.FeaturedPost');
  return (
    <div className="fieldnotes-reading-spread">
      <div className="fieldnotes-reading-margin">
        <PostBreadcrumb title={post.title} />
        <PostDecorator readingTime={post.readingTime} tag={post.tags[0]} />
        <h1
          className="post-title"
          dangerouslySetInnerHTML={{ __html: post.titleHtml }}
        />
        <Excerpt excerpt={post.excerpt} />
        <div className="fieldnotes-author-block">
          <p className="post-meta-label">{t('WrittenBy')}</p>
          <div className="post-author" id="author">
            <PostAvatar
              name={author.name}
              avatar={author.avatar}
              className="post-author-avatar"
            />
            <div className="post-author-details">
              <p className="post-author-name">{author.name}</p>
              {author.title && (
                <p className="post-author-title">{author.title}</p>
              )}
            </div>
          </div>
          <PublishedDate {...post} hasTitle={!!author.title} />
          <Sharing {...post} url={postUrl} />
        </div>
        <PostAside toc={post.toc} likeCount={post.likeCount} postId={post.id} />
      </div>
      <div className="fieldnotes-reading-page">
        <CoverImage src={post.coverImage} />
        {children}
      </div>
    </div>
  );
}
